/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const PhoneNumberTab = defineMessages({
  titleOne: {
    id: 'phone.PhoneNumber.titleOne',
    defaultMessage: 'Manage phone numbers'
  },
  phoneNumberLabel: {
    id: 'phone.PhoneNumber.phoneNumberLabel',
    defaultMessage: 'Phone number'
  },
  stepDescription: {
    id: 'phone.PhoneNumber.stepDescription',
    defaultMessage:
      'Create a primary phone number with your provider that your assistant will use to send and receive calls.'
  },
  descriptionOnePartOne: {
    id: 'phone.PhoneNumber.descriptionOnePartOne',
    defaultMessage:
      'Create one or multiple phone numbers with your provider that your assistant will use to send and receive calls.'
  },
  descriptionOnePartTwo: {
    id: 'phone.PhoneNumber.descriptionOnePartTwo',
    defaultMessage:
      'Note: Calling and texting integrations can be used together. <link>Learn more</link>'
  },
  descriptionTwoPartOne: {
    id: 'phone.PhoneNumber.descriptionTwoPartOne',
    defaultMessage:
      'Your assistant will send and receive calls at the number below. After the number is ready (this may take several minutes), give it a call!'
  },
  descriptionTwoPartTwo: {
    id: 'phone.PhoneNumber.descriptionTwoPartTwo',
    defaultMessage:
      'By pairing with <link>SMS with Twilio</link>, you can enable your assistant to send and receive texts with a call provider of your choice.'
  },
  intelepeerChecking: {
    id: 'phone.PhoneNumber.intelepeerChecking',
    defaultMessage: 'Checking...'
  },
  intelepeerWaiting: {
    id: 'phone.PhoneNumber.intelepeerWaiting',
    defaultMessage: 'Provisioning...'
  },
  intelepeerReady: {
    id: 'phone.PhoneNumber.intelepeerReady',
    defaultMessage: 'Ready'
  },
  intelepeerNotReady: {
    id: 'phone.PhoneNumber.intelepeerNotReady',
    defaultMessage: 'Failed'
  }
});

export const PhoneNumberStepMessages = defineMessages({
  phoneNumberPlaceholder: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberPlaceholder',
    defaultMessage: 'Enter a phone number: +1-234-567-8901'
  },
  phoneNumberHelperText: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberHelperText',
    defaultMessage:
      'This number must not be in use by another Watson Assistant phone integration.'
  },
  phoneNumberLabel: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberLabel',
    defaultMessage: 'At what number will your assistant send and receive calls?'
  },
  phoneNumberLabelContactCenter: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberLabelContactCenter',
    defaultMessage: '{contactCenter} phone number'
  },
  phoneNumberContactCenterSubtitle: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberContactCenterSubtitle',
    defaultMessage:
      'Bring in your primary phone number with {contactCenter} for your assistant to receive calls.'
  },
  phoneNumberContactCenterNote: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberContactCenterNote',
    defaultMessage: 'Note: You can add additional phone numbers later on.'
  },
  phoneNumberContactCenterTitle: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberContactCenterTitle',
    defaultMessage: 'Setup phone number'
  },
  phoneNumberContactCenterSIP: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberContactCenterSIP',
    defaultMessage: `Copy this SIP address and add it to your Amazon Chime Voice Connector origination configuration.`
  },
  phoneNumberContactCenterSIPLabel: {
    id: 'phone.PhoneNumberStepMessages.phoneNumberContactCenterSIPLabel',
    defaultMessage: 'Host'
  }
});

export const PhoneNumbers = defineMessages({
  phoneNumber: {
    id: 'phone.PhoneNumbers.phoneNumber',
    defaultMessage: 'Phone number'
  },
  description: {
    id: 'phone.PhoneNumbers.description',
    defaultMessage: 'Description'
  },
  status: {
    id: 'phone.PhoneNumbers.status',
    defaultMessage: 'Status'
  },
  search: {
    id: 'phone.PhoneNumbers.search',
    defaultMessage: 'Search'
  },
  searchPlaceholder: {
    id: 'phone.PhoneNumbers.searchPlaceholder',
    defaultMessage: 'Search by number or description'
  },
  download: {
    id: 'phone.PhoneNumbers.download',
    defaultMessage: 'Download all numbers into a CSV file'
  },
  addPhoneNumber: {
    id: 'phone.PhoneNumbers.addPhoneNumber',
    defaultMessage: 'Add phone number'
  },
  emptyStateIcon: {
    id: 'phone.PhoneNumbers.emptyStateIcon',
    defaultMessage: 'Empty state icon'
  },
  emptyTableTitle: {
    id: 'phone.PhoneNumbers.emptyTableTitle',
    defaultMessage: "You haven't added a phone number"
  },
  emptyTableDescription: {
    id: 'phone.PhoneNumbers.emptyTableDescription',
    defaultMessage: "When you add a number it'll show up here."
  },
  rowNumInUse: {
    id: 'phone.PhoneNumbers.rowNumInUse',
    defaultMessage: 'Error: in use by {name}'
  },
  setPrimary: {
    id: 'phone.PhoneNumbers.setPrimary',
    defaultMessage: 'Set as Primary'
  }
});

export const PhoneImportModalMessages = defineMessages({
  importResults: {
    id: 'phone.PhoneImportModal.importResults',
    defaultMessage:
      'Imported file results in {num} valid and unique phone entries'
  },
  importDuplicates: {
    id: 'phone.PhoneImportModal.importDuplicates',
    defaultMessage: 'Import found and ignored {num} duplicate phone numbers'
  },
  upload: {
    id: 'phone.PhoneImportModal.upload',
    defaultMessage: 'Upload a CSV file'
  },
  apply: {
    id: 'phone.PhoneImportModal.apply',
    defaultMessage: 'Apply'
  },
  importInstructOne: {
    id: 'phone.PhoneImportModal.importInstructOne',
    defaultMessage:
      'Add phone numbers by importing a comma separated value (CSV) file that lists the phone numbers.'
  },
  importInstructTwo: {
    id: 'phone.PhoneImportModal.importInstructTwo',
    defaultMessage:
      'The file must contain one entry per line with the phone number as the first value, and an optional description as the second. Any additional values specified per line are ignored.'
  },
  importTitle: {
    id: 'phone.PhoneImportModal.importTitle',
    defaultMessage: 'Import from CSV'
  },
  uploadLabelChoose: {
    id: 'phone.PhoneImportModal.uploadLabelChoose',
    defaultMessage: 'Drag and drop file here or click to select a file'
  },
  importDelete: {
    id: 'phone.PhoneImportModal.importDelete',
    defaultMessage: 'Clear CSV File'
  },
  importError: {
    id: 'phone.PhoneImportModal.importError',
    defaultMessage: 'Error occurred importing phone CSV file:'
  },
  emptyPhoneError: {
    id: 'phone.PhoneImportModal.emptyPhoneError',
    defaultMessage: 'Empty phone field'
  },
  importPhoneError: {
    id: 'phone.PhoneImportModal.importPhoneError',
    defaultMessage: 'CSV Validation Error, phone field, line {lineNo}: {errMsg}'
  },
  importDescError: {
    id: 'phone.PhoneImportModal.importDescError',
    defaultMessage:
      'CSV Validation Error, description field, line {lineNo}: {errMsg}'
  }
});

export const PhoneAddModal = defineMessages({
  title: {
    id: 'phone.PhoneAddModal.title',
    defaultMessage: 'Add phone number'
  },
  subtitle: {
    id: 'phone.PhoneAddModal.subtitle',
    defaultMessage:
      'Phone numbers should be at least 4 and no more than 15 digits long, include country extension, and only contain:'
  },
  phoneValidCharacterOne: {
    id: 'phone.PhoneAddModal.phoneValidCharacterOne',
    defaultMessage: 'Number characters (0-9)'
  },
  phoneValidCharacterTwo: {
    id: 'phone.PhoneAddModal.phoneValidCharacterTwo',
    defaultMessage: 'Plus (+) and minus (-)'
  },
  phoneValidCharacterThree: {
    id: 'phone.PhoneAddModal.phoneValidCharacterThree',
    defaultMessage: "Parentheses '(' and ')'"
  },
  phoneNumberLabel: {
    id: 'phone.PhoneAddModal.phoneNumberLabel',
    defaultMessage: 'Enter phone number'
  },
  phoneNumberPlaceholder: {
    id: 'phone.PhoneAddModal.phoneNumberPlaceholder',
    defaultMessage: '+1 (234) 567-8901'
  },
  phoneDescriptionLabel: {
    id: 'phone.PhoneAddModal.phoneDescriptionLabel',
    defaultMessage: 'Enter description'
  },
  phoneDescriptionPlaceholder: {
    id: 'phone.PhoneAddModal.phoneDescriptionPlaceholder',
    defaultMessage: 'Company phone number...'
  },
  numberInUse: {
    id: 'phone.PhoneNumbers.numberInUse',
    defaultMessage: 'Number is already used by assistant: {name}'
  },
  duplicateNumber: {
    id: 'phone.PhoneAddModal.duplicateNumber',
    defaultMessage: 'Number is already in use.'
  }
});

export const ContactCenterPhoneNumberTab = defineMessages({
  amazonDescriptionOnePartOne: {
    id: 'phone.ContactCenterPhoneNumberTab.amazonDescriptionOnePartOne',
    defaultMessage: `To integrate your assistant with Amazon Connect, you'll need to login to your <link>AWS Account</link> `
  },
  amazonDescriptionOnePartTwo: {
    id: 'phone.ContactCenterPhoneNumberTab.amazonDescriptionOnePartTwo',
    defaultMessage: 'and follow these <link>steps</link>.'
  },
  amazonDescriptionTwo: {
    id: 'phone.ContactCenterPhoneNumberTab.amazonDescriptionTwo',
    defaultMessage:
      'Add the Amazon Connect phone number(s) that your assistant will use to receive calls. '
  },
  amazonDescriptionTwoNote: {
    id: 'phone.ContactCenterPhoneNumberTab.amazonDescriptionTwoNote',
    defaultMessage:
      'Note: Calling and texting integrations can be used together. <link>Learn more</link>'
  },
  titleContactCenter: {
    id: 'phone.ContactCenterPhoneNumberTab.titleContactCenter',
    defaultMessage: 'Phone connection'
  }
});
